import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { formatValue } from './utils';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Block = styled.div`
  padding: 6px 10px;
  border-radius: 4px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${({ variant }) =>
    variant === 'before'
      ? 'var(--if-diff-remove-bg)'
      : 'var(--if-diff-add-bg)'};
  border: 1px solid
    ${({ variant }) =>
      variant === 'before'
        ? 'var(--if-diff-remove-border)'
        : 'var(--if-diff-add-border)'};
  color: ${({ variant }) =>
    variant === 'before'
      ? 'var(--if-diff-remove-ink)'
      : 'var(--if-diff-add-ink)'};
`;

const Prefix = styled.span`
  color: var(--if-ink-2);
`;

const ChangedSegment = styled.span`
  font-weight: 700;
`;

const UnchangedSegment = styled.span`
  opacity: 0.6;
`;

const Label = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: var(--if-ink-3);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

function parseUrl(url) {
  const str = formatValue(url);
  const match = str.match(/^(https?:\/\/)?(.*)$/);
  if (!match) return { protocol: '', segments: [str] };
  return {
    protocol: match[1] || '',
    segments: match[2].split('/'),
  };
}

function renderSegments(segments, otherSegments, variant) {
  return segments.map((seg, i) => {
    const changed = seg !== otherSegments[i];
    const Comp = changed ? ChangedSegment : UnchangedSegment;
    return (
      <React.Fragment key={`${variant}-${String(i)}`}>
        {i > 0 && <UnchangedSegment>/</UnchangedSegment>}
        <Comp>{seg}</Comp>
      </React.Fragment>
    );
  });
}

export default function UrlDiff({ from, to }) {
  const fromParsed = parseUrl(from);
  const toParsed = parseUrl(to);

  return (
    <Wrapper>
      <Label>before</Label>
      <Block variant="before">
        <Prefix>{fromParsed.protocol}</Prefix>
        {renderSegments(fromParsed.segments, toParsed.segments, 'from')}
      </Block>
      <Label style={{ marginTop: 4 }}>&darr; changed to</Label>
      <Block variant="after">
        <Prefix>{toParsed.protocol}</Prefix>
        {renderSegments(toParsed.segments, fromParsed.segments, 'to')}
      </Block>
    </Wrapper>
  );
}

UrlDiff.propTypes = {
  from: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
  ]).isRequired,
  to: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
  ]).isRequired,
};
